import { keys } from "@arti/utilities";
import { useEffect, useState } from "react";

import type {
  UsageBucketUnit,
  UsageEntriesResponse,
  UsageEntry,
  UsageEntryOrder,
  UsageFilters,
  UsageSummaryResponse,
  UsageTotals,
} from "../types/usage";

import {
  getUsageSummaryAsUser,
  listUsageEntriesAsUser,
} from "../dashboard-client";
import { MS_IN_DAY } from "../utils/usageConstants";

const DEFAULT_WINDOW_DAYS = 30;

export interface UsageStatsState {
  filters: Required<Pick<UsageFilters, "fromTimestampMs" | "toTimestampMs">> &
    Omit<UsageFilters, "fromTimestampMs" | "toTimestampMs">;
  includeBreakdowns: boolean;
  includeTimeseries: boolean;
  bucketUnit: UsageBucketUnit | null;
  order: UsageEntryOrder;
  page: number;
  pageSize: number;
  summary: UsageSummaryResponse | null;
  entries: UsageEntriesResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export interface UseUsageStatsOptions {
  /**
   * Note: options are only read on first render; update the hook key/reset externally if you need to apply changes.
   */
  initialFilters?: UsageFilters;
  includeBreakdowns?: boolean;
  includeTimeseries?: boolean;
  bucketUnit?: UsageBucketUnit | null;
  debounceMs?: number;
  initialPageSize?: number;
  initialOrder?: UsageEntryOrder;
}

export interface UseUsageStatsResult extends UsageStatsState {
  totals: UsageTotals | null;
  entriesRows: UsageEntry[];
  totalRows: number;
  pageCount: number;
  setFilters: (patch: UsageFilters) => void;
  setDateRange: (fromTimestampMs: number, toTimestampMs: number) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  refresh: () => void;
  reset: () => void;
}

interface FetchUsageSummaryOptions {
  filters?: UsageFilters;
  includeTimeseries?: boolean;
  includeBreakdowns?: boolean;
  bucketUnit?: UsageBucketUnit | null;
}

interface FetchUsageEntriesOptions {
  filters?: UsageFilters;
  page?: number;
  pageSize?: number;
  order?: UsageEntryOrder;
}

interface UsageApi {
  fetchUsageSummary: (
    options?: FetchUsageSummaryOptions,
  ) => Promise<UsageSummaryResponse>;
  fetchUsageEntries: (
    options?: FetchUsageEntriesOptions,
  ) => Promise<UsageEntriesResponse>;
}

function buildUsageFiltersQuery(filters: UsageFilters | undefined): {
  from?: string;
  to?: string;
  provider?: string;
  model?: string;
  stage?: string;
  threadId?: string;
  taskId?: string;
  ticketId?: string;
} {
  if (!filters) {
    return {};
  }

  return {
    from:
      typeof filters.fromTimestampMs === "number"
        ? String(filters.fromTimestampMs)
        : undefined,
    model: filters.model,
    provider: filters.provider,
    stage: filters.stage,
    taskId: filters.taskId,
    threadId: filters.threadId,
    ticketId: filters.ticketId,
    to:
      typeof filters.toTimestampMs === "number"
        ? String(filters.toTimestampMs)
        : undefined,
  };
}

function booleanToQueryParam(
  value: boolean | undefined,
): "true" | "false" | undefined {
  if (typeof value !== "boolean") {
    return undefined;
  }
  return value ? "true" : "false";
}

async function fetchUsageSummary(
  options: FetchUsageSummaryOptions = {},
): Promise<UsageSummaryResponse> {
  const { data } = await getUsageSummaryAsUser({
    query: {
      ...buildUsageFiltersQuery(options.filters),
      bucketUnit: options.bucketUnit ?? undefined,
      includeBreakdowns: booleanToQueryParam(options.includeBreakdowns),
      includeTimeseries: booleanToQueryParam(options.includeTimeseries),
    },
  });
  return data;
}

async function fetchUsageEntries(
  options: FetchUsageEntriesOptions = {},
): Promise<UsageEntriesResponse> {
  const { data } = await listUsageEntriesAsUser({
    query: {
      ...buildUsageFiltersQuery(options.filters),
      order: options.order,
      page: typeof options.page === "number" ? String(options.page) : undefined,
      pageSize:
        typeof options.pageSize === "number"
          ? String(options.pageSize)
          : undefined,
    },
  });
  return data;
}

export function useUsageStats(
  options: UseUsageStatsOptions = {},
): UseUsageStatsResult {
  const [controller] = useState(
    () =>
      new UsageStatsController({
        api: { fetchUsageEntries, fetchUsageSummary },
        bucketUnit: options.bucketUnit,
        debounceMs: options.debounceMs,
        includeBreakdowns: options.includeBreakdowns,
        includeTimeseries: options.includeTimeseries,
        initialFilters: options.initialFilters,
        initialOrder: options.initialOrder,
        initialPageSize: options.initialPageSize,
      }),
  );

  const [state, setState] = useState<UsageStatsState>(() =>
    controller.getSnapshot(),
  );

  useEffect(() => {
    const unsubscribe = controller.subscribe(() =>
      setState(controller.getSnapshot()),
    );
    controller.start();
    return () => {
      unsubscribe();
      controller.dispose();
    };
  }, [controller]);

  const totals = state.summary?.totals ?? null;
  const entriesRows = state.entries?.entries ?? [];
  const totalRows = state.entries?.total ?? 0;
  const pageCount = state.entries
    ? Math.max(1, Math.ceil(state.entries.total / state.entries.pageSize))
    : 1;

  return {
    ...state,
    entriesRows,
    nextPage: controller.nextPage,
    pageCount,
    prevPage: controller.prevPage,
    refresh: controller.refresh,
    reset: controller.reset,
    setDateRange: controller.setDateRange,
    setFilters: controller.setFilters,
    setPage: controller.setPage,
    setPageSize: controller.setPageSize,
    totalRows,
    totals,
  };
}

export class UsageStatsController {
  private readonly api: UsageApi;
  private readonly debounceMs: number;
  private readonly defaultState: UsageStatsState;

  private state: UsageStatsState;
  private listeners = new Set<() => void>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private requestId = 0;
  private hasStarted = false;

  constructor(options: {
    api: UsageApi;
    initialFilters?: UsageFilters;
    includeBreakdowns?: boolean;
    includeTimeseries?: boolean;
    bucketUnit?: UsageBucketUnit | null;
    debounceMs?: number;
    initialPageSize?: number;
    initialOrder?: UsageEntryOrder;
  }) {
    this.api = options.api;
    this.debounceMs =
      typeof options.debounceMs === "number"
        ? Math.max(0, Math.trunc(options.debounceMs))
        : 250;

    const now = Date.now();
    const defaultRange = buildDefaultDateRange(now);
    const mergedFilters: UsageStatsState["filters"] = {
      fromTimestampMs: defaultRange.fromTimestampMs,
      toTimestampMs: defaultRange.toTimestampMs,
      ...options.initialFilters,
    };

    this.defaultState = {
      bucketUnit: options.bucketUnit ?? null,
      entries: null,
      error: null,
      filters: mergedFilters,
      includeBreakdowns: options.includeBreakdowns !== false,
      includeTimeseries: options.includeTimeseries === true,
      isLoading: false,
      order: options.initialOrder ?? "desc",
      page: 1,
      pageSize:
        typeof options.initialPageSize === "number"
          ? Math.max(1, Math.trunc(options.initialPageSize))
          : 50,
      summary: null,
    };

    this.state = { ...this.defaultState };
  }

  start() {
    if (this.hasStarted) {
      return;
    }
    this.hasStarted = true;
    this.refresh();
  }

  dispose() {
    this.clearDebounce();
    this.listeners.clear();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getSnapshot(): UsageStatsState {
    return this.state;
  }

  reset = () => {
    this.state = {
      ...this.defaultState,
      entries: null,
      error: null,
      isLoading: false,
      summary: null,
    };
    this.emit();
    this.refresh();
  };

  setFilters = (patch: UsageFilters) => {
    const nextFilters = applyFilterPatch(this.state.filters, patch);
    const filtersChanged = !shallowEqual(this.state.filters, nextFilters);
    if (!filtersChanged) {
      return;
    }

    this.state = { ...this.state, filters: nextFilters, page: 1 };
    this.emit();
    this.refreshDebounced();
  };

  setDateRange = (fromTimestampMs: number, toTimestampMs: number) => {
    this.setFilters({ fromTimestampMs, toTimestampMs });
  };

  setPage = (page: number) => {
    const next = Math.max(1, Math.trunc(page));
    if (next === this.state.page) {
      return;
    }
    this.state = { ...this.state, page: next };
    this.emit();
    this.refresh({ immediate: true });
  };

  setPageSize = (pageSize: number) => {
    const next = Math.max(1, Math.trunc(pageSize));
    if (next === this.state.pageSize) {
      return;
    }
    this.state = { ...this.state, page: 1, pageSize: next };
    this.emit();
    this.refresh({ immediate: true });
  };

  nextPage = () => {
    const total = this.state.entries?.total ?? 0;
    const pageSize = this.state.entries?.pageSize ?? this.state.pageSize;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    this.setPage(Math.min(pageCount, this.state.page + 1));
  };

  prevPage = () => {
    this.setPage(Math.max(1, this.state.page - 1));
  };

  refresh = (options: { immediate?: boolean } = {}) => {
    if (options.immediate) {
      this.clearDebounce();
      void this.fetchNow();
      return;
    }
    this.refreshDebounced();
  };

  private refreshDebounced() {
    this.clearDebounce();
    if (this.debounceMs === 0) {
      void this.fetchNow();
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.fetchNow();
    }, this.debounceMs);
  }

  private clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private emit() {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private async fetchNow(): Promise<void> {
    const currentRequestId = (this.requestId += 1);
    const snapshot = this.state;

    this.state = { ...snapshot, error: null, isLoading: true };
    this.emit();

    try {
      const { filters } = snapshot;
      const [summary, entries] = await Promise.all([
        this.api.fetchUsageSummary({
          bucketUnit: snapshot.bucketUnit,
          filters,
          includeBreakdowns: snapshot.includeBreakdowns,
          includeTimeseries: snapshot.includeTimeseries,
        }),
        this.api.fetchUsageEntries({
          filters,
          order: snapshot.order,
          page: snapshot.page,
          pageSize: snapshot.pageSize,
        }),
      ]);

      if (currentRequestId !== this.requestId) {
        return;
      }

      this.state = {
        ...this.state,
        entries,
        error: null,
        isLoading: false,
        summary,
      };
      this.emit();
    } catch (error) {
      if (currentRequestId !== this.requestId) {
        return;
      }

      this.state = {
        ...this.state,
        error: error instanceof Error ? error : new Error("Unknown error"),
        isLoading: false,
      };
      this.emit();
    }
  }
}

function buildDefaultDateRange(nowMs: number): {
  fromTimestampMs: number;
  toTimestampMs: number;
} {
  const toTimestampMs = Math.max(0, Math.trunc(nowMs));
  const fromTimestampMs = Math.max(
    0,
    toTimestampMs - DEFAULT_WINDOW_DAYS * MS_IN_DAY,
  );
  return { fromTimestampMs, toTimestampMs };
}

function applyFilterPatch(
  current: UsageStatsState["filters"],
  patch: UsageFilters,
): UsageStatsState["filters"] {
  const merged = { ...current, ...patch };
  const next: UsageStatsState["filters"] = {
    fromTimestampMs: merged.fromTimestampMs,
    toTimestampMs: merged.toTimestampMs,
  };

  for (const key of [
    "provider",
    "model",
    "stage",
    "threadId",
    "taskId",
    "ticketId",
  ] as const) {
    const value = merged[key];
    if (value === undefined) {
      continue;
    }
    next[key] = value;
  }

  return next;
}

function shallowEqual<T extends object>(a: T, b: T): boolean {
  const aKeys = keys(a);
  const bKeys = keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  for (const key of aKeys) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}

export const __testing = {
  DEFAULT_WINDOW_DAYS,
  MS_IN_DAY,
  UsageStatsController,
  buildDefaultDateRange,
};
